'use client';

import { memo, useState } from 'react';
import Image from 'next/image';
import { Loader2 } from 'lucide-react';
import {
  Plus,
  CheckCircle2,
  Circle,
  MoreVertical,
  Pencil,
  Copy,
  Trash2,
} from 'lucide-react';
import { useTranslations } from 'next-intl';
import { Button } from '@/shared/ui/shadcn/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/shared/ui/shadcn/dropdown-menu';
import { cn } from '@/shared/lib/cn';
import type { Control } from '@/types/concept';
import type { RenderControl } from '@/types/render';

interface ControlGridProps {
  controls: Control[];
  selectedControlIds: Array<RenderControl | string>; // Support both formats
  /**
   * Backdrop image URL for the *selected* card (passive cards stay on white).
   * Source switches by render-form mode upstream:
   * angle photo in Explore, parent render image in Refine.
   */
  backdropUrl?: string | null;
  unusedControlsCount: number;
  maxUnusedControls: number;
  onControlToggle: (controlId: string) => void;
  onEditControl: (control: Control) => void;
  onCopyAsNewControl: (control: Control) => void;
  onDeleteControl: (control: Control) => void;
  onCreateControl: () => void;
  hasValidFile: (control: Control) => boolean;
}

export const ControlGrid = memo(function ControlGridComponent({
  controls,
  selectedControlIds,
  backdropUrl,
  unusedControlsCount,
  maxUnusedControls,
  onControlToggle,
  onEditControl,
  onCopyAsNewControl,
  onDeleteControl,
  onCreateControl,
  hasValidFile,
}: ControlGridProps) {
  const t = useTranslations('render.settings.control');

  const filteredControls = controls.filter((control) => hasValidFile(control));

  const isLimitReached = unusedControlsCount >= maxUnusedControls;

  return (
    <div>
      {/* Apple parity: a single 2-column grid (Draw new + custom controls)
          with no section header. */}
      <div className="grid grid-cols-2 gap-4">
        {/* Add New Control Card — matches Apple "Draw new" card (4:3, dashed, plus icon) */}
        <button
          onClick={onCreateControl}
          disabled={isLimitReached}
          className={cn(
            'relative aspect-[4/3] rounded-lg border-2 border-dashed transition-all w-full',
            isLimitReached
              ? 'border-muted-foreground/20 bg-muted/50 cursor-not-allowed opacity-50'
              : 'border-muted-foreground/30 hover:border-primary bg-background hover:bg-primary/5 cursor-pointer'
          )}
          title={
            isLimitReached
              ? t('controlLimitReached', { max: maxUnusedControls })
              : t('createNewControl')
          }
        >
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-2">
            <Plus
              className={cn(
                'w-8 h-8',
                isLimitReached
                  ? 'text-muted-foreground/50'
                  : 'text-primary'
              )}
            />
            <span className="text-list-item-subtitle text-muted-foreground">
              {isLimitReached ? t('limitReached') : t('newControl')}
            </span>
          </div>
        </button>

        {/* Existing Controls */}
        {filteredControls.map((control) => {
          const isSelected = selectedControlIds.some((item) =>
            typeof item === 'string'
              ? item === control.id
              : item.id === control.id
          );
          const canDelete = !control.attributes.is_used_in_renders;

          return (
            <div key={control.id} className="flex flex-col w-full">
              <div
                onClick={() => onControlToggle(control.id)}
                className={cn(
                  'relative aspect-[4/3] rounded-lg overflow-hidden border-2 transition-all cursor-pointer w-full',
                  isSelected
                    ? 'border-primary'
                    : 'border-border hover:border-border/80'
                )}
              >
                <ControlCardImage
                  control={control}
                  backdropUrl={isSelected ? backdropUrl ?? undefined : undefined}
                />

                {isSelected && (
                  <div className="absolute top-2 end-2 w-6 h-6 bg-primary rounded-full flex items-center justify-center">
                    <svg
                      className="w-4 h-4 text-primary-foreground"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fillRule="evenodd"
                        d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </div>
                )}

                {/* Overflow menu — matches render-card three-dot button.
                    Wrapper carries the touch target (~44px via p-2 padding)
                    so a slightly-off tap doesn't fall through to the card's
                    toggle handler and shift the trigger sideways. Pointer-
                    and mouse-down propagation are stopped because Radix
                    dismissal layers listen on those, not on click. */}
                <div
                  className={cn(
                    'absolute top-0 z-10 p-2',
                    isSelected ? 'end-8' : 'end-0'
                  )}
                  onClick={(e) => e.stopPropagation()}
                  onPointerDown={(e) => e.stopPropagation()}
                  onMouseDown={(e) => e.stopPropagation()}
                >
                  <DropdownMenu modal={false}>
                    <DropdownMenuTrigger asChild>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7 rounded-full bg-background/80 hover:bg-background text-foreground shadow-sm backdrop-blur-sm"
                        title={t('edit')}
                      >
                        <MoreVertical className="h-4 w-4" />
                        <span className="sr-only">{t('edit')}</span>
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-48">
                      <DropdownMenuItem
                        onClick={() => onEditControl(control)}
                        className="cursor-pointer"
                      >
                        <Pencil className="me-2 h-4 w-4" />
                        {t('edit')}
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        onClick={() => onCopyAsNewControl(control)}
                        disabled={isLimitReached}
                        className="cursor-pointer"
                      >
                        <Copy className="me-2 h-4 w-4" />
                        {t('copyAsNew')}
                      </DropdownMenuItem>
                      {canDelete && (
                        <>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem
                            onClick={() => onDeleteControl(control)}
                            className="text-destructive focus:text-destructive cursor-pointer"
                          >
                            <Trash2 className="me-2 h-4 w-4" />
                            {t('deleteControl')}
                          </DropdownMenuItem>
                        </>
                      )}
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              </div>

              {/* Label with usage indicator (below card, like Android/Apple) */}
              <div className="flex items-center gap-1 mt-1">
                {isSelected ? (
                  <>
                    <CheckCircle2 className="w-3 h-3 text-green-600 dark:text-green-500 shrink-0" />
                    <span className="text-card-meta text-muted-foreground truncate">
                      {t('labelCustom')}
                    </span>
                  </>
                ) : !control.attributes.is_used_in_renders ? (
                  <>
                    <Circle className="w-3 h-3 text-warning shrink-0" />
                    <span className="text-card-meta text-warning truncate">
                      {t('labelNotUsed')}
                    </span>
                  </>
                ) : null}
              </div>
            </div>
          );
        })}
      </div>

      {/* Unused controls counter badge at the bottom (like Android) */}
      {unusedControlsCount > 0 && (
        <div className="mt-3">
          <span
            className={cn(
              'text-chip px-2 py-1 rounded',
              isLimitReached
                ? 'bg-warning/15 text-warning'
                : 'bg-muted text-muted-foreground'
            )}
          >
            {t('unusedControls', {
              count: unusedControlsCount,
              max: maxUnusedControls,
            })}
          </span>
        </div>
      )}
    </div>
  );
});

/**
 * Control card image. Backdrop is drawn only on the *selected* card (caller
 * passes `undefined` for passive cards) so the inventory reads as a clean
 * grid of strokes on white. The selected card waits for the control overlay
 * to load before fading in the backdrop, to avoid a flash of the bare backdrop.
 */
function ControlCardImage({
  control,
  backdropUrl,
}: {
  control: Control;
  backdropUrl?: string;
}) {
  const [controlLoaded, setControlLoaded] = useState(false);
  const hasControlFile = control.attributes.file && control.attributes.file.url;

  return (
    <>
      {/* Flat surface under every card so strokes read cleanly. Black in dark
          mode keeps a poorly-transparent/light backdrop photo dark instead of
          glowing as a bright white block. */}
      <div className="absolute inset-0 bg-white dark:bg-black" />

      {/* Backdrop — only on the selected card, fades in once the control
          overlay has loaded (or when there's no control file). */}
      {(controlLoaded || !hasControlFile) && backdropUrl && (
        <Image
          src={backdropUrl}
          alt=""
          fill
          sizes="(max-width: 640px) 50vw, 200px"
          className="object-cover opacity-30 saturate-[0.25] brightness-95"
          onError={(e) => {
            (e.target as HTMLImageElement).style.display = 'none';
          }}
        />
      )}

      {/* Control overlay (transparent PNG) — object-cover to stay aligned with bg */}
      {hasControlFile && (
        <Image
          src={control.attributes.file!.url}
          alt={`${control.attributes.control_type} control`}
          fill
          sizes="(max-width: 640px) 50vw, 200px"
          className="object-cover"
          onLoad={() => setControlLoaded(true)}
          onError={(e) => {
            console.error(
              'Failed to load control image:',
              control.id,
              control.attributes.file?.url
            );
            (e.target as HTMLImageElement).style.display = 'none';
          }}
        />
      )}

      {/* Spinner while control image is loading */}
      {hasControlFile && !controlLoaded && (
        <div className="absolute inset-0 flex items-center justify-center">
          <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
        </div>
      )}
    </>
  );
}
